import { withDevtools } from '@ngrx-toolkit/core';
import { computed, inject, effect, isDevMode } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import type { UserDto } from '@mb/auth';
import type { Session } from './auth-http.service';
import { AuthHttpService } from './auth-http.service';
import { clientLogger } from '@mb/common/client';
import { isBrowser } from '@mb/common/client';
import { NgxPermissionsService } from 'ngx-permissions';

function authHttpErrorMessage(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    const body = error.error;
    if (typeof body === 'string') return body;
    if (body && typeof body === 'object') {
      if (
        'error' in body &&
        typeof (body as { error: unknown }).error === 'string'
      ) {
        return (body as { error: string }).error;
      }
      if (
        'message' in body &&
        typeof (body as { message: unknown }).message === 'string'
      ) {
        return (body as { message: string }).message;
      }
    }
    return error.message || 'Request failed';
  }
  if (error instanceof Error) return error.message;
  return 'Request failed';
}

function isUnauthenticatedSessionError(error: unknown): boolean {
  if (error instanceof HttpErrorResponse) {
    return error.status === 401;
  }
  if (error && typeof error === 'object' && 'status' in error) {
    return (error as { status: number }).status === 401;
  }
  return false;
}

function logAuthStoreDebug(message: string, error?: unknown): void {
  if (!isDevMode()) {
    return;
  }

  if (error === undefined) {
    clientLogger.error(message);
    return;
  }

  clientLogger.error(message, {
    error:
      error instanceof Error ? error : new Error(authHttpErrorMessage(error)),
  });
}

function safeSignOutRedirect(callbackUrl?: string): string {
  if (!callbackUrl) {
    return '/';
  }

  return callbackUrl.startsWith('/') && !callbackUrl.startsWith('//')
    ? callbackUrl
    : '/';
}

/**
 * AuthStore — session uses `httpResource` on {@link AuthHttpService}; login/register/password
 * actions use `HttpClient` mutations + `patchState` (see Angular Architects Resource + Signal Store).
 */

interface AuthState {
  user: UserDto | null;
  session: Session | null;
  error: string | null;
  isInitialized: boolean;
  /** True while email/password sign-in request is in flight. */
  signInPending: boolean;
  /** True while register request is in flight. */
  registerPending: boolean;
  /** True while forgot-password request is in flight. */
  forgotPasswordPending: boolean;
  /** True while reset-password confirmation is in flight. */
  resetPasswordPending: boolean;
  /** True while verify-email request is in flight. */
  verifyEmailPending: boolean;
  /** True while resend-verification request is in flight. */
  resendVerificationPending: boolean;
}

const initialState: AuthState = {
  user: null,
  session: null,
  error: null,
  isInitialized: false,
  signInPending: false,
  registerPending: false,
  forgotPasswordPending: false,
  resetPasswordPending: false,
  verifyEmailPending: false,
  resendVerificationPending: false,
};

export const AuthStore = signalStore(
  withDevtools('auth store'),
  withState(initialState),
  withComputed((state) => {
    const authService = inject(AuthHttpService);

    return {
      // ============================================
      // Authentication Status
      // ============================================
      isAuthenticated: computed(() => !!state.session()),

      // ============================================
      // Loading States (session/refresh resources + mutation flags)
      // ============================================
      isInitializing: computed(() =>
        authService.sessionResourceAccessor.isLoading(),
      ),
      isRefreshing: computed(() =>
        authService.refreshResourceAccessor.isLoading(),
      ),
      isCheckingSession: computed(() =>
        authService.sessionResourceAccessor.isLoading(),
      ),
      isSigningIn: computed(() => state.signInPending()),
      isRegistering: computed(() => state.registerPending()),
      isRequestingPasswordReset: computed(() => state.forgotPasswordPending()),
      isResettingPassword: computed(() => state.resetPasswordPending()),
      isVerifyingEmail: computed(() => state.verifyEmailPending()),
      isResendingVerification: computed(() =>
        state.resendVerificationPending(),
      ),

      // ============================================
      // Error States
      // ============================================
      hasError: computed(
        () => !!state.error() || !!authService.sessionResourceAccessor.error(),
      ),

      // ============================================
      // Resource States
      // ============================================
      sessionStatus: computed(() =>
        authService.sessionResourceAccessor.status(),
      ),
      sessionValue: computed(() => {
        const status = authService.sessionResourceAccessor.status();
        if (status === 'resolved') {
          try {
            return authService.sessionResourceAccessor.value() ?? null;
          } catch {
            return null;
          }
        }
        return null;
      }),
      sessionError: computed(() => authService.sessionResourceAccessor.error()),

      refreshStatus: computed(() =>
        authService.refreshResourceAccessor.status(),
      ),

      // ============================================
      // Extended Session Data
      // ============================================
      userRoles: computed(() => state.session()?.roles || []),
      userPermissions: computed(() => state.session()?.permissions || []),
      userPreferences: computed(() => state.session()?.preferences || {}),
      isProfileComplete: computed(
        () => state.session()?.profileComplete || false,
      ),
      accountStatus: computed(
        () => state.session()?.accountStatus || 'unknown',
      ),
    };
  }),
  withMethods((store) => {
    const authService = inject(AuthHttpService);
    const permissionsService = inject(NgxPermissionsService);

    const requestPasswordResetEmail = async (
      email: string,
    ): Promise<boolean> => {
      clientLogger.info('[AUTH STORE] Requesting password reset...');
      patchState(store, { error: null, forgotPasswordPending: true });
      try {
        const value = await authService.requestPasswordReset(email);
        if ('message' in value) {
          return true;
        }
        patchState(store, {
          error:
            'error' in value &&
            typeof (value as { error?: string }).error === 'string'
              ? (value as { error: string }).error
              : 'auth.forgotPassword.errors.requestFailed',
        });
        return false;
      } catch (error) {
        patchState(store, {
          error:
            authHttpErrorMessage(error) ||
            'auth.forgotPassword.errors.requestFailed',
        });
        return false;
      } finally {
        patchState(store, { forgotPasswordPending: false });
      }
    };

    return {
      // ============================================
      // Session Management
      // ============================================

      /**
       * Reload session - triggers the httpResource to refetch
       */
      reloadSession: () => {
        clientLogger.info('[AUTH STORE] Reloading session...');
        authService.reloadSession();
      },

      /**
       * Check session (alias for reloadSession)
       */
      checkSession: async (): Promise<Session | null> => {
        clientLogger.info('[AUTH STORE] Checking session...');
        authService.reloadSession();
        return store.session();
      },

      /**
       * Refresh tokens - triggers the refresh httpResource
       */
      refreshTokens: () => {
        clientLogger.info('[AUTH STORE] Refreshing tokens...');
        authService.reloadRefreshToken();
      },

      /**
       * Hydrate auth store with server-loaded session data
       * Used to avoid SSR/hydration timing issues
       */
      hydrateFromServer: (session: Session) => {
        clientLogger.info('[AUTH STORE] Hydrating from server data', {
          email: session.user.email,
        });

        permissionsService.loadPermissions(session.permissions);
        patchState(store, {
          session,
          user: session.user,
          error: null,
          isInitialized: true,
        });
      },

      // ============================================
      // Authentication Actions (HttpClient mutations + patchState)
      // ============================================

      signInWithEmail: async (
        email: () => string | undefined,
        password: () => string | undefined,
      ): Promise<boolean> => {
        const emailVal = email();
        const passwordVal = password();
        if (!emailVal || !passwordVal) {
          return false;
        }
        clientLogger.info('[AUTH STORE] Signing in with email...');
        patchState(store, { error: null, signInPending: true });
        try {
          const value = await authService.login(emailVal, passwordVal);
          if (value?.success && value.data) {
            clientLogger.info('[AUTH STORE] Sign in successful');
            authService.reloadSession();
            return true;
          }
          patchState(store, { error: value?.error || 'Sign in failed' });
          return false;
        } catch (error) {
          patchState(store, { error: authHttpErrorMessage(error) });
          return false;
        } finally {
          patchState(store, { signInPending: false });
        }
      },

      register: async (
        email: string,
        password: string,
        name?: string,
      ): Promise<boolean> => {
        clientLogger.info('[AUTH STORE] Registering new user...');
        patchState(store, { error: null, registerPending: true });
        try {
          const value = await authService.register(email, password, name);
          if (value?.success && value.data) {
            clientLogger.info('[AUTH STORE] Registration successful');
            authService.reloadSession();
            return true;
          }
          patchState(store, {
            error: value?.error || 'auth.register.errors.failedGeneric',
          });
          return false;
        } catch (error) {
          patchState(store, { error: authHttpErrorMessage(error) });
          return false;
        } finally {
          patchState(store, { registerPending: false });
        }
      },

      exchangeAuthCode: async (code: string): Promise<boolean> => {
        clientLogger.info('[AUTH STORE] Exchanging authorization code...');
        patchState(store, { error: null });
        try {
          const value = await authService.exchangeAuthCode(code);
          if ('success' in value && value.success) {
            clientLogger.info('[AUTH STORE] Code exchange successful');
            // Let the browser apply Set-Cookie from the exchange response before /session.
            queueMicrotask(() => authService.reloadSession());
            return true;
          }
          patchState(store, {
            error:
              'error' in value && value.error
                ? String(value.error)
                : 'Code exchange failed',
          });
          return false;
        } catch (error) {
          patchState(store, { error: authHttpErrorMessage(error) });
          return false;
        }
      },

      /**
       * Sign out
       */
      signOut: async (callbackUrl?: string) => {
        clientLogger.info('[AUTH STORE] Signing out...');

        // Clear local state immediately
        patchState(store, {
          session: null,
          user: null,
          error: null,
          isInitialized: false,
        });

        // Make the signout request (fire and forget)
        try {
          await fetch('/api/auth/signout', {
            method: 'POST',
            credentials: 'include',
          });
        } catch (error) {
          clientLogger.error('[AUTH STORE] Sign out error', {
            error: error instanceof Error ? error : new Error(String(error)),
          });
        }

        window.location.href = safeSignOutRedirect(callbackUrl);
      },

      // ============================================
      // Password Management
      // ============================================

      changePassword: async (
        currentPassword: string,
        newPassword: string,
      ): Promise<boolean> => {
        clientLogger.info('[AUTH STORE] Changing password...');
        patchState(store, { error: null });
        try {
          const value = await authService.changePassword(
            currentPassword,
            newPassword,
          );
          if ('success' in value && value.success) {
            clientLogger.info('[AUTH STORE] Password changed successfully');
            return true;
          }
          patchState(store, {
            error:
              'error' in value &&
              typeof (value as { error?: string }).error === 'string'
                ? (value as { error: string }).error
                : 'Password change failed',
          });
          return false;
        } catch (error) {
          patchState(store, { error: authHttpErrorMessage(error) });
          return false;
        }
      },

      resetPassword: async (email: string): Promise<boolean> => {
        return requestPasswordResetEmail(email);
      },

      forgotPassword: async (email: string): Promise<boolean> => {
        return requestPasswordResetEmail(email);
      },

      confirmPasswordReset: async (
        email: string,
        resetToken: string,
        newPassword: string,
      ): Promise<boolean> => {
        clientLogger.info('[AUTH STORE] Confirming password reset...');
        patchState(store, { error: null, resetPasswordPending: true });
        try {
          const value = await authService.confirmPasswordReset(
            email,
            resetToken,
            newPassword,
          );
          if ('message' in value) {
            return true;
          }
          patchState(store, {
            error:
              'error' in value &&
              typeof (value as { error?: string }).error === 'string'
                ? (value as { error: string }).error
                : 'auth.forgotPassword.errors.resetFailed',
          });
          return false;
        } catch (error) {
          patchState(store, {
            error:
              authHttpErrorMessage(error) ||
              'auth.forgotPassword.errors.resetFailed',
          });
          return false;
        } finally {
          patchState(store, { resetPasswordPending: false });
        }
      },

      // ============================================
      // Email Verification (TODO: Add resources)
      // ============================================

      /**
       * Verify email with token
       */
      verifyEmail: async (token: string, email: string): Promise<boolean> => {
        patchState(store, { error: null, verifyEmailPending: true });
        try {
          const result = await authService.verifyEmail(token, email);
          if ('message' in result) {
            authService.reloadSession();
            return true;
          }
          patchState(store, {
            error: result.error || 'auth.verifyEmail.failed',
          });
          return false;
        } catch (error) {
          patchState(store, {
            error: authHttpErrorMessage(error) || 'auth.verifyEmail.failed',
          });
          return false;
        } finally {
          patchState(store, { verifyEmailPending: false });
        }
      },

      /**
       * Resend verification email
       */
      resendVerification: async (email: string): Promise<boolean> => {
        patchState(store, { error: null, resendVerificationPending: true });
        try {
          const result = await authService.resendVerification(email);
          if ('message' in result) {
            return true;
          }
          patchState(store, {
            error: result.error || 'auth.resendVerification.failed',
          });
          return false;
        } catch (error) {
          patchState(store, {
            error:
              authHttpErrorMessage(error) || 'auth.resendVerification.failed',
          });
          return false;
        } finally {
          patchState(store, { resendVerificationPending: false });
        }
      },

      clearError: () => {
        patchState(store, { error: null });
      },

      // ============================================
      // OAuth Providers
      // ============================================

      /**
       * Sign in with OAuth provider
       */
      signInWithProvider: (provider: string, callbackUrl?: string) => {
        const url = `/api/auth/signin/${provider}${callbackUrl ? `?callbackUrl=${encodeURIComponent(callbackUrl)}` : ''}`;
        window.location.href = url;
      },

      /**
       * Guard helper to require authentication
       */
      requireAuth: (provider: string, redirectUrl?: string): boolean => {
        if (!store.isAuthenticated()) {
          const url = `/api/auth/signin/${provider}${redirectUrl ? `?callbackUrl=${encodeURIComponent(redirectUrl)}` : ''}`;
          window.location.href = url;
          return false;
        }
        return true;
      },

      // ============================================
      // Role and Permission Checks
      // ============================================

      /**
       * Check if user has a specific role
       */
      hasRole: (role: string): boolean => {
        return store.userRoles().includes(role);
      },

      /**
       * Check if user has a specific permission
       */
      hasPermission: (permission: string): boolean => {
        return store.userPermissions().includes(permission);
      },

      /**
       * Check if user has any of the specified roles
       */
      hasAnyRole: (...roles: string[]): boolean => {
        return roles.some((role) => store.userRoles().includes(role));
      },

      /**
       * Check if user has all of the specified roles
       */
      hasAllRoles: (...roles: string[]): boolean => {
        return roles.every((role) => store.userRoles().includes(role));
      },
    };
  }),
  withHooks({
    onInit(store) {
      const authService = inject(AuthHttpService);
      const permissionsService = inject(NgxPermissionsService);

      clientLogger.info('[AUTH STORE] Initializing auth state...');

      // Trigger session load in browser (resource won't load during SSR).
      // Skip on OAuth callback while `code` is still in the URL — the callback page
      // exchanges the code and sets cookies before loading the session.
      if (isBrowser()) {
        const path = window.location.pathname;
        const hasOAuthCode = new URLSearchParams(window.location.search).has(
          'code',
        );
        const onOAuthCallback = path.includes('/auth/callback') && hasOAuthCode;
        if (!onOAuthCallback) {
          authService.reloadSession();
        }
      }

      // ============================================
      // Watch Session Resource
      // ============================================
      effect(() => {
        // Skip during SSR - resource won't load anyway
        if (!isBrowser()) {
          return;
        }

        const status = authService.sessionResourceAccessor.status();
        const error = authService.sessionResourceAccessor.error();

        clientLogger.info('[AUTH STORE] Session resource status', { status });

        if (status === 'resolved') {
          let value: Session | null = null;
          try {
            value = authService.sessionResourceAccessor.value() ?? null;
          } catch (err) {
            logAuthStoreDebug(
              '[AUTH STORE] Error accessing session value:',
              err,
            );
          }

          if (value && value.user) {
            clientLogger.info('[AUTH STORE] ✅ Session loaded for user', {
              email: value.user.email,
            });
            permissionsService.loadPermissions(value.permissions ?? []);
            patchState(store, {
              session: value,
              user: value.user,
              error: null,
              isInitialized: true,
            });
          } else {
            clientLogger.warn('[AUTH STORE] ⚠️ No session found');
            patchState(store, {
              session: null,
              user: null,
              error: null,
              isInitialized: true,
            });
          }
        } else if (status === 'error') {
          if (isUnauthenticatedSessionError(error)) {
            clientLogger.info('[AUTH STORE] No active session (not signed in)');
          } else {
            logAuthStoreDebug('[AUTH STORE] ❌ Session resource error:', error);
          }
          patchState(store, {
            error: null,
            session: null,
            user: null,
            isInitialized: true,
          });
        }
        // If status is 'loading' or 'idle', don't set isInitialized yet
      });

      // ============================================
      // Auto-Refresh Tokens (with cross-tab coordination)
      // ============================================
      if (isBrowser()) {
        // Use BroadcastChannel to coordinate refreshes across tabs
        let refreshChannel: BroadcastChannel | null = null;
        let isRefreshing = false;
        let lastRefreshTime = 0;
        let currentRefreshId: string | null = null;
        const REFRESH_COOLDOWN = 4.5 * 60 * 1000; // 4.5 minutes (slightly less than 5 to avoid race conditions)

        try {
          refreshChannel = new BroadcastChannel('auth-token-refresh');

          // Listen for refresh events from other tabs
          refreshChannel.onmessage = (event) => {
            if (event.data.type === 'refresh-started') {
              // Another tab is refreshing, mark as refreshing and update timestamp
              isRefreshing = true;
              lastRefreshTime = Date.now();
              currentRefreshId = event.data.id;
            } else if (event.data.type === 'refresh-completed') {
              // Another tab completed refresh, reload our session
              if (event.data.id === currentRefreshId) {
                isRefreshing = false;
                lastRefreshTime = Date.now();
                currentRefreshId = null;
                authService.reloadSession();
              }
            }
          };
        } catch {
          // BroadcastChannel not supported (older browsers), fall back to no coordination
          clientLogger.warn(
            '[AUTH STORE] BroadcastChannel not supported, cross-tab coordination disabled',
          );
        }

        // Watch for refresh completion
        let previousLoadingState = false;
        effect(() => {
          const isLoading = authService.refreshResourceAccessor.isLoading();

          // Detect when refresh transitions from loading to not loading
          if (
            previousLoadingState &&
            !isLoading &&
            isRefreshing &&
            refreshChannel
          ) {
            // Refresh completed, notify other tabs
            if (refreshChannel) {
              try {
                refreshChannel.postMessage({
                  type: 'refresh-completed',
                  id: currentRefreshId,
                  timestamp: Date.now(),
                });
              } catch {
                // Ignore errors
              }
            }
            isRefreshing = false;
            lastRefreshTime = Date.now();
            currentRefreshId = null;
          }

          previousLoadingState = isLoading;
        });

        const refreshInterval = setInterval(
          () => {
            if (
              !store.isAuthenticated() ||
              authService.refreshResourceAccessor.isLoading()
            ) {
              return;
            }

            // Check if another tab is already refreshing or we just refreshed
            const timeSinceLastRefresh = Date.now() - lastRefreshTime;
            if (isRefreshing || timeSinceLastRefresh < REFRESH_COOLDOWN) {
              return;
            }

            // Generate unique ID for this refresh
            const refreshId = `${Date.now()}-${Math.random()}`;
            currentRefreshId = refreshId;

            // Try to claim the refresh (prevent other tabs from refreshing)
            if (refreshChannel) {
              try {
                refreshChannel.postMessage({
                  type: 'refresh-started',
                  id: refreshId,
                  timestamp: Date.now(),
                });
                isRefreshing = true;
              } catch {
                // If posting fails, continue anyway
              }
            } else {
              // No channel, just mark as refreshing locally
              isRefreshing = true;
            }

            // Perform the refresh
            authService.reloadRefreshToken();
          },
          5 * 60 * 1000,
        ); // Every 5 minutes

        // Store interval and channel for cleanup
        (store as any)._refreshInterval = refreshInterval;
        (store as any)._refreshChannel = refreshChannel;
      }
    },
    onDestroy(store) {
      // Clean up auto-refresh interval
      if ((store as any)._refreshInterval) {
        clearInterval((store as any)._refreshInterval);
      }
      // Clean up BroadcastChannel
      if ((store as any)._refreshChannel) {
        try {
          (store as any)._refreshChannel.close();
        } catch {
          // Ignore errors
        }
      }
    },
  }),
);
